import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../common/hooks';
import { useLogoutHandler } from '../../../common/hooks/useLogoutHandler';
import { resetAdviceContinuity } from '../../../features/ews';
import { ADVISOR_ROUTES } from '../../../routes/routeConstants';
import { AlertDialog } from '../AlertDialog/AlertDialog';
import { Nav } from '../Nav';
import { ConfirmationCentered } from '../ConfirmationDialog/ConfirmationDialog';
import { getClientJourneyFlowType } from '../../../utils';
import { cancelAllEwsStreams } from '../../../utils/ewsUnifiedEventStream';

export const Header = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [popupOpen, setPopupOpen] = useState(false);
    const { user } = useAppSelector((state) => state.auth);
    const [showConsoleNavConfirmation, setShowConsoleNavConfirmation] = useState(false);
    const [pendingNavigationPath, setPendingNavigationPath] = useState<string | null>(null);
    const { handleLogout } = useLogoutHandler();
    const { enableConfirmConsoleNavigation = false } = useAppSelector((state) => state.adviceClient);
    const location = useLocation();
    const path = location.pathname;
    const planPaths = [
        ADVISOR_ROUTES.CLIENT_PROFILE,
        ADVISOR_ROUTES.RISK_TOLERANCE,
        ADVISOR_ROUTES.GOALS,
        ADVISOR_ROUTES.FUNDING_NAVIGATOR,
        ADVISOR_ROUTES.PLAN_DETAILS,
        ADVISOR_ROUTES.PLAN_EXECUTION
    ];
    const { error } = useAppSelector((state) => state.auth);
    const prevPathRef = useRef(path);

    // A late EWS result would overwrite the next plan's goals, so cancel it once the user leaves the plan.
    useEffect(() => {
        if (planPaths.includes(prevPathRef.current) && !planPaths.includes(path)) {
            cancelAllEwsStreams();
        }
        prevPathRef.current = path;
    }, [path]);

    const onNavItemsClicked = (route: string) => {
        // Use below for custom logic on particular navbar routes before navigating
        // switch(route) {
        //     case ADVISOR_ROUTES.ADVISOR_CONSOLE:
        //     case ADVISOR_ROUTES.RECENT_UPDATES:
        //     case ADVISOR_ROUTES.ADMIN:
        // }
        // handling Leave/Stay(PendingNavigationPath) modal for all navItems
        if (enableConfirmConsoleNavigation && planPaths.includes(path)) {
            setPendingNavigationPath(route);
            setShowConsoleNavConfirmation(true);
        } else {
            navigate(route);
        }
    };

    const onConfirmLeavePlanDetails = () => {
        setShowConsoleNavConfirmation(false);
        if (pendingNavigationPath) {
            navigate(pendingNavigationPath);
            setPendingNavigationPath(null);
        }
    };

    const tenant = useAppSelector((state) => state.auth?.user?.tenant || state.global?.globalConfig?.subdomain);
    const isFlow2 = getClientJourneyFlowType(tenant) === 'flow2';

    const navItems = [
        {
            label: `${t('LABEL_CONSOLE')}`,
            onClick: () => {
                // Leaving a plan back to the console ends its reallocation context.
                dispatch(resetAdviceContinuity());
                onNavItemsClicked(ADVISOR_ROUTES.ADVISOR_CONSOLE);
            }
        },
        {
            label: `${t('LABEL_RECENT_UPDATES')}`,
            onClick: () => onNavItemsClicked(ADVISOR_ROUTES.RECENT_UPDATES)
        }
    ];

    // Insert "Resources" nav item for Flow2 at the 2nd position.
    if (isFlow2) {
        navItems.splice(1, 0, {
            label: `${t('LABEL_RESOURCES')}`,
            onClick: () => onNavItemsClicked(ADVISOR_ROUTES.RESOURCES)
        });
    }

    const handleDismiss = () => {
        setPopupOpen(false);
    };
    return (
        <>
            <Nav
                items={error?.message === 'Rejected' ? [] : navItems}
                user={user}
                onClickLogout={() => setPopupOpen(true)}
                onClickAdmin={() => onNavItemsClicked(ADVISOR_ROUTES.ADMIN)}
            />
            <AlertDialog
                type="confirmation"
                open={popupOpen}
                icon="logout"
                handleClose={handleDismiss}
                content={`${t('LOGOUT_MODAL_CONTENT')}`}
                title={t('TEXT_MENU_LOGOUT')}
                aria-label="log-out-confirmation-modal"
                data-testid="log-out-confirmation-modal"
                cancelButtonLabel={t('DELETE_CONFIRMATION_MODAL_CANCEL_TEXT')}
                confirmButtonLabel={t('TEXT_MENU_LOGOUT')}
                onCancellation={handleDismiss}
                onConfirmation={handleLogout}
            />
            {showConsoleNavConfirmation && (
                <ConfirmationCentered
                    onCancellation={() => setShowConsoleNavConfirmation(false)}
                    onConfirmation={onConfirmLeavePlanDetails}
                    open={showConsoleNavConfirmation}
                    content={t('CONSOLE_NAVIGATION_CONFIRMATION_BODY')}
                    confirmButtonLabel={t('CONSOLE_NAVIGATION_CONFIRMATION_LEAVE')}
                    cancelButtonLabel={t('CONSOLE_NAVIGATION_CONFIRMATION_STAY')}
                    title={t('CONSOLE_NAVIGATION_CONFIRMATION_TITLE')}
                    width={'100%'}
                    cardMaxWidth="350px"
                    onCloseIconClick={() => setShowConsoleNavConfirmation(false)}
                />
            )}
        </>
    );
};
export default Header;
